import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    subscribe,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    wrapper?: InjectableCss;
    inputField?: InjectableCss;
    titleLabel?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerText?: TypographyPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            background-color: ${UCSColors.sageGreen02};
            align-items: center;
            color: ${UCSColors.gray10};
            padding: 79px 0;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 20px;
                        text-align: center;
                    `,
                    css`
                        padding: 32px 20px;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    inputField: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            /* border: 2px solid #86b29e; */
        `,
    },
    titleLabel: {
        variant: "h2",
    },
    descriptionText: {
        variant: "h5",
        transform: "uppercase",
        css: css`
            margin: 16px 0 24px;
            letter-spacing: 0.1em;
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                max-width: 300px;
                color: ${UCSColors.gray05};

                input {
                    border: 2px solid #86b29e !important;
                }
            `,
        },
    },
    emailButton: {
        color: "secondary",
        css: css`
            width: 200px;
            border: 2px solid #86b29e;
            border-radius: 0;
            height: 40px;
            margin-left: -2px;
        `,
        typographyProps: {
            transform: "uppercase",
            size: 14,
            weight: 400,
            color: UCSColors.gray10,
        },
    },
    disclaimerText: {
        css: css`
            font-size: 12px;
            margin-top: 24px;
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

const styles = subscribeStyles;

const emailRegexp = new RegExp("^\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*$");

const CmsSubscribe = ({ subscribe }: Props) => {
    const emailRequiredFieldMessage = siteMessage("Newsletter_EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("Newsletter_EmailSubscription_EmailIsInvalidErrorMessage");
    const emailFieldPlaceholder = siteMessage("Newsletter_Email_Placeholder") as string;

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const title = siteMessage("Newsletter_Title") as string;
    const subtitle = siteMessage("Newsletter_Subtitle") as string;
    const disclaimer = siteMessage("Newsletter_Disclaimer") as string;
    const cta = siteMessage("Newsletter_Cta") as string;
    const link = siteMessage("Newsletter_Disclaimer_Link") as string;
    const linkLabel = siteMessage("Newsletter_Disclaimer_Link_Label") as string;
    const disclaimerParts = disclaimer.split(linkLabel);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            toasterContext.addToast({
                body: emailError,
                messageType: "danger",
            });
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("Newsletter_Email_Subscribe_Success"),
                    messageType: "success",
                });
                setEmail("");
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleLabel}>{translate(title)}</Typography>
            <Typography {...styles.descriptionText}>{translate(subtitle)}</Typography>
            <StyledWrapper {...styles.inputField}>
                <TextField
                    {...styles.emailTextField}
                    id="subscribe"
                    placeholder={emailFieldPlaceholder || "Enter email address"}
                    inputMode="email"
                    value={email}
                    autoCapitalize="off"
                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                />
                <Button {...styles.emailButton} onClick={onSubscribeClick} disabled={isSubmitted && !!emailError}>
                    {cta}
                </Button>
            </StyledWrapper>
            <Typography {...styles.disclaimerText}>
                {disclaimerParts[0]}
                <Link href={link} color="#86B29E" hoverMode="lighten">
                    {linkLabel}
                </Link>
                {disclaimerParts[1]}
            </Typography>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};
const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [],
    },
};

export default widgetModule;
